// src/pages/ManagePatient/index.jsx

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { useState, useEffect } from "react";
import PageShell from "../../components/PageShell";
import PatientInQueue from "../PatientInQueue";
import PatientsTable from "../frontDesk/PatientsTable";
import InPatientsTable from "../frontDesk/InPatientsTable";
import { useAuth } from "../../context/AuthContext.jsx";
import { getDoctorByUserId } from "../../services/doctorService";
import { useSearchParams } from "react-router-dom";
import OpdReports from "../inPatientDetail/OpdReports";
import IpdReports from "../inPatientDetail/IpdReports";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import Pharmacy from "../inventory/Pharmacy/index.jsx";
import {
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
  TAB_PANEL_SX,
} from "../../utils/pageLayoutStyles";

const ManagePatient = () => {
  const { user } = useAuth();
  const { hasCapability, isPremiumPackage } = useSubscriptionCapabilities();
  const role = String(user?.roleName || "").toLowerCase();
  const isPharmacyAdmin = role === "pharmacy admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "1";
  const [selectedTab, setSelectedTab] = useState(tabFromUrl);

  const hasOpdCapability = hasCapability("OPD");
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  // IN patient tab: Premium only (Basic & Intermediate stay OUT-only)
  const canUseInPatient = isPremiumPackage && hasIpdCapability;

  const [doctorId, setDoctorId] = useState(null);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  // Already-fetched patient details handed over by the table row that was
  // clicked, so the timeline doesn't have to re-fetch GET /patients/{id}.
  const [selectedPatientInfo, setSelectedPatientInfo] = useState(null);
  const [showPatientTimeline, setShowPatientTimeline] = useState(false);
  const [showIpdTimeline, setShowIpdTimeline] = useState(false);
  const [patientType, setPatientType] = useState(null);
  const [showPharmacyTimeline, setShowPharmacyTimeline] = useState(false);

  useEffect(() => {
    if (user && (user.roleName === "doctor" || user.roleName === "surgeon")) {
      // Fast path: doctorId is cached on the user object at login (see login/index.jsx),
      // so the queue can render immediately instead of waiting on a fresh doctor lookup
      // (which has an expensive org-scanning fallback) on every mount.
      let cachedDoctorId = user.doctorId;
      if (!cachedDoctorId) {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            cachedDoctorId = JSON.parse(storedUser)?.doctorId;
          } catch (e) {
            // ignore parse errors, fall through to lookup
          }
        }
      }
      if (cachedDoctorId && !isNaN(cachedDoctorId) && Number(cachedDoctorId) > 0) {
        setDoctorId(Number(cachedDoctorId));
        return;
      }

      const fetchDoctorId = async () => {
        try {
          let currentUser = user;
          if (!user.organizationId) {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
              try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser?.organizationId) {
                  currentUser = { ...user, organizationId: parsedUser.organizationId };
                } else {
                  const orgId = localStorage.getItem("organizationId");
                  if (orgId && !isNaN(orgId) && Number(orgId) > 0) {
                    const updatedUser = { ...user, organizationId: Number(orgId) };
                    localStorage.setItem("user", JSON.stringify(updatedUser));
                    currentUser = updatedUser;
                  }
                }
              } catch (e) {
                console.error("Error parsing stored user:", e);
              }
            }
          }

          const doctorDetails = await getDoctorByUserId(currentUser.id);
          if (doctorDetails && doctorDetails.id) {
            setDoctorId(doctorDetails.id);
            // Cache doctorId (and organizationId, if still missing) so future mounts
            // skip this lookup entirely via the fast path above.
            const updatedUser = {
              ...currentUser,
              doctorId: doctorDetails.id,
              organizationId: currentUser.organizationId || doctorDetails.organizationId,
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(new CustomEvent('userUpdated', { detail: { updatedUser } }));
          } else {
            setDoctorId(null);
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            setDoctorId(null);
          } else {
            console.error("Error fetching doctor details:", error);
            setDoctorId(null);
          }
        }
      };
      fetchDoctorId();
    } else {
      setDoctorId(null);
    }
  }, [user]);

  useEffect(() => {
    setSelectedTab(tabFromUrl);
  }, [tabFromUrl]);

  // Basic/Intermediate: In Patients tab is Premium-only
  useEffect(() => {
    if (!canUseInPatient && selectedTab === "2") {
      setSelectedTab(hasOpdCapability ? "3" : "1");
      setSearchParams({ tab: hasOpdCapability ? "3" : "1" }, { replace: true });
    }
  }, [canUseInPatient, hasOpdCapability, selectedTab, setSearchParams]);

  // Pharmacy admin header search / patient-name click → full OPD or IPD timeline
  useEffect(() => {
    if (!isPharmacyAdmin) return;

    const patientIdFromUrl = searchParams.get("patientId");
    const typeFromUrl = searchParams.get("type");

    if (patientIdFromUrl) {
      setSelectedPatientId(patientIdFromUrl);
      setPatientType(typeFromUrl === "ipd" ? "ipd" : "opd");
      setShowPharmacyTimeline(true);
    }
  }, [isPharmacyAdmin, searchParams]);

  // Deep link from elsewhere (e.g. an invoice's linked appointment/prescription) → open this
  // patient's OPD/IPD timeline directly under the matching tab, instead of landing on the bare
  // table. Doctor/surgeon only — pharmacy admin is handled by the effect above.
  useEffect(() => {
    if (isPharmacyAdmin) return;

    const patientIdFromUrl = searchParams.get("patientId");
    if (!patientIdFromUrl) return;

    if (searchParams.get("type") === "ipd") {
      setShowIpdTimeline(true);
    } else {
      setShowPatientTimeline(true);
    }
    setSelectedPatientId(patientIdFromUrl);
  }, [isPharmacyAdmin, searchParams]);

  // Strip the deep-link query params once the timeline is closed, so "Back" doesn't
  // immediately reopen it and a page refresh doesn't get stuck on the timeline view.
  const clearPatientDeepLinkParams = () => {
    if (!searchParams.get("patientId") && !searchParams.get("type")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("patientId");
    nextParams.delete("type");
    nextParams.delete("timelineTab");
    setSearchParams(nextParams, { replace: true });
  };

  const handleChange = (event, newValue) => {
    setSelectedTab(newValue);
    setSearchParams({ tab: newValue });
    setShowPatientTimeline(false);
    setShowIpdTimeline(false);
    setSelectedPatientId(null);
  };

  const handlePatientClick = (patientId, patientInfo = null) => {
    setSelectedPatientId(patientId);
    setSelectedPatientInfo(patientInfo);
    setShowPatientTimeline(true);
  };

  const handleIpdPatientClick = (patientId) => {
    setSelectedPatientId(patientId);
    setShowIpdTimeline(true);
  };

  const handlePharmacyPatientClick = (patientId, type) => {
    setSelectedPatientId(patientId);
    setPatientType(type);
    setShowPharmacyTimeline(true);
  };

  const handlePharmacyBack = () => {
    setShowPharmacyTimeline(false);
    setSelectedPatientId(null);
    setPatientType(null);
    clearPatientDeepLinkParams();
  };

  // Pharmacy admin: show Pharmacy patients list (former Inventory Pharmacy tab)
  if (isPharmacyAdmin) {
    return (
      <PageShell title="PATIENTS" paperSx={{ mt: { md: -1.5 } }}>
        {showPharmacyTimeline && selectedPatientId ? (
          patientType === "ipd" ? (
            <IpdReports patientId={selectedPatientId} onBack={handlePharmacyBack} />
          ) : (
            <OpdReports patientId={selectedPatientId} onBack={handlePharmacyBack} />
          )
        ) : (
          <Pharmacy onPatientClick={handlePharmacyPatientClick} hideMainTitle />
        )}
      </PageShell>
    );
  }

  return (
    <PageShell title="PATIENTS" paperSx={{ mt: { md: -1.5 } }}>
      <TabContext value={selectedTab}>
        <Box sx={PAGE_TABS_BAR_SX}>
          <TabList
            {...PAGE_TAB_LIST_PROPS}
            onChange={handleChange}
          >
            <Tab label="Patients In Queue" value="1" />
            {canUseInPatient && <Tab label="In Patients" value="2" />}
            {hasOpdCapability && <Tab label="Out Patients" value="3" />}
          </TabList>
        </Box>

        <TabPanel value="1" sx={TAB_PANEL_SX}>
          <PatientInQueue doctorId={doctorId} />
        </TabPanel>
        {canUseInPatient && (
          <TabPanel value="2" sx={TAB_PANEL_SX}>
            {showIpdTimeline && selectedPatientId ? (
              <IpdReports
                patientId={selectedPatientId}
                onBack={() => {
                  setShowIpdTimeline(false);
                  setSelectedPatientId(null);
                  clearPatientDeepLinkParams();
                }}
              />
            ) : (
              <InPatientsTable onPatientClick={handleIpdPatientClick} />
            )}
          </TabPanel>
        )}
        {hasOpdCapability && (
          <TabPanel value="3" sx={TAB_PANEL_SX}>
            {showPatientTimeline && selectedPatientId ? (
              <OpdReports
                patientId={selectedPatientId}
                initialPatientInfo={selectedPatientInfo}
                onBack={() => {
                  setShowPatientTimeline(false);
                  setSelectedPatientId(null);
                  setSelectedPatientInfo(null);
                  clearPatientDeepLinkParams();
                }}
              />
            ) : (
              <PatientsTable
                doctorId={doctorId}
                onPatientClick={handlePatientClick}
              />
            )}
          </TabPanel>
        )}
      </TabContext>
    </PageShell>
  );
};

export default ManagePatient;
